import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import api from "../api";

interface User {
  id: number;
  name: string;
  avatar_url?: string;
  role: "viewer" | "contributor" | "admin";
  department?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoggedIn: boolean;
  canUpload: boolean;
  canViewInternal: boolean;
  loading: boolean;
  login: () => void;
  devLogin: (name?: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isLoggedIn: false,
  canUpload: false,
  canViewInternal: false,
  loading: true,
  login: () => {},
  devLogin: async () => {},
  logout: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

const TOKEN_KEY = "credit-intel-token";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor to include token
  useEffect(() => {
    const interceptor = api.interceptors.request.use((config) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) {
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });
    return () => api.interceptors.request.eject(interceptor);
  }, []);

  // Check for token in URL params (SSO callback redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) {
      localStorage.setItem(TOKEN_KEY, urlToken);
      setToken(urlToken);
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Fetch current user on mount / token change
  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    api
      .get("/auth/me", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setUser(r.data.data);
      })
      .catch(() => {
        // Token invalid/expired
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const login = useCallback(() => {
    // Redirect to WeCom SSO
    api.get("/auth/login/wecom").then((r) => {
      const url = r.data.data.url;
      if (url) window.location.href = url;
    }).catch(() => {
      // SSO not configured, try dev login
      alert("企业微信 SSO 未配置，请使用开发模式登录");
    });
  }, []);

  const devLogin = useCallback(async (name = "开发者") => {
    // Security: disable dev login in production
    if (import.meta.env.PROD) {
      console.warn("Dev login disabled in production environment");
      return;
    }
    try {
      const r = await api.post(`/auth/dev-login?name=${encodeURIComponent(name)}&role=contributor&department=产品研发`);
      const { token: t, user: u } = r.data.data;
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setUser(u);
    } catch (e) {
      console.error("Dev login failed:", e);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const isLoggedIn = user !== null;
  const canUpload = isLoggedIn && (user?.role === "contributor" || user?.role === "admin");
  const canViewInternal = canUpload;

  return (
    <AuthContext.Provider
      value={{ user, token, isLoggedIn, canUpload, canViewInternal, loading, login, devLogin, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
